import * as React from 'react'
import { connect } from 'react-redux'
import MaskedTextInput from 'react-text-mask'

import { fetchCityStateFromZip } from 'common/actions'
import {
  applicationFieldLoadingEnd,
  applicationFieldLoadingStart,
  applicationFieldValidate,
} from 'common/actions/application'
import { FieldDataValue, Mask } from 'embed/constants'
import { applicationStateSelector } from 'embed/selectors/application'
import { uiSelectedProductSelector } from 'embed/selectors/ui'
import { Filter, Product } from 'even-ts-static'

export interface OwnProps {
  className?: string
  disabled?: boolean
  forceFocus?: boolean
  onBlur: (value: React.FormEvent<HTMLInputElement>) => void
  placeholder?: string
  required?: boolean
}

interface StoreProps {
  city?: FieldDataValue
  product: Product
  stateAbbr?: FieldDataValue
  zipcode?: FieldDataValue
}

interface DispatchProps {
  dispatchApplicationFieldValidate: (
    product: Product,
    key: string,
    value: FieldDataValue,
    shouldShowValidationStatus: boolean,
  ) => void
  dispatchFetchCityStateFromZip: (Product, zipcode: string) => Promise<void>
  dispatchFieldLoadingStart: (Product) => void
  dispatchFieldLoadingEnd: (Product) => void
}

export type ApplicationZipcodeFieldProps = OwnProps & StoreProps & DispatchProps
const ConnectedApplicationZipcodeField: React.FC<ApplicationZipcodeFieldProps> = ({
  dispatchApplicationFieldValidate,
  dispatchFetchCityStateFromZip,
  dispatchFieldLoadingStart,
  dispatchFieldLoadingEnd,
  product,
  zipcode,
  placeholder,
  forceFocus,
  city,
  stateAbbr,
  ...props
}) => {
  React.useEffect(() => {
    if (city !== undefined) {
      dispatchApplicationFieldValidate(product, Filter.City, city, true)
    }
    if (stateAbbr !== undefined) {
      dispatchApplicationFieldValidate(product, Filter.State, stateAbbr, true)
    }
  }, [city, stateAbbr])

  const onBlur = React.useCallback(
    (value: React.FormEvent<HTMLInputElement>) => {
      props.onBlur(value)
      if (zipcode !== undefined) {
        dispatchFieldLoadingStart(product)
        dispatchFetchCityStateFromZip(product, zipcode as string)
          .then(() => {
            dispatchFieldLoadingEnd(product)
          })
          .catch(dispatchFieldLoadingEnd)
      }
    },
    [props.onBlur, zipcode, product, dispatchFieldLoadingStart, dispatchFetchCityStateFromZip, dispatchFieldLoadingEnd],
  )

  return (
    <MaskedTextInput mask={Mask.ZipCode} placeholder={placeholder} {...props} onBlur={onBlur} autoFocus={forceFocus} />
  )
}

const mapStateToProps = (state): StoreProps => {
  const product = uiSelectedProductSelector(state) as Product
  const fields = applicationStateSelector(state)?.[product ?? '']?.fields
  const city = fields?.[Filter.City]?.value
  const stateAbbr = fields?.[Filter.State]?.value
  const zipcode = fields?.[Filter.Zipcode]?.value

  return {
    city,
    product,
    stateAbbr,
    zipcode,
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCityStateFromZip: (product: Product, zipcode) => dispatch(fetchCityStateFromZip(product, zipcode)),
  dispatchApplicationFieldValidate: (
    product: Product,
    key: string,
    value: FieldDataValue,
    shouldShowValidationStatus: boolean,
  ) => dispatch(applicationFieldValidate(product, key, value, true)),
  dispatchFieldLoadingEnd: (product: Product) => {
    dispatch(applicationFieldLoadingEnd(product, Filter.City))
    dispatch(applicationFieldLoadingEnd(product, Filter.State))
  },
  dispatchFieldLoadingStart: (product: Product) => {
    dispatch(applicationFieldLoadingStart(product, Filter.City))
    dispatch(applicationFieldLoadingStart(product, Filter.State))
  },
})

export const ApplicationZipcodeField = connect<StoreProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(ConnectedApplicationZipcodeField)
